import { afterNextRender, ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';

import { ErrorHandlerService } from '@core/errors/error-handler.service';
import { ModalType } from '@core/models/modal.types';
import { BaseActionHandlerService } from '@core/services/action-handler';
import { ContentService } from '@core/services/content.service';
import { JexlService } from '@core/services/jexl.service';
import { ModalService } from '@core/services/modal.service';

import { type DynamicAction } from '@shared/registry';
import {
  type ErrorDisplayConfig,
  UniversalModalTemplate,
} from '@shared/universal-modal-template/universal-modal-template';
import type { UniversalModalContent } from '@shared/universal-modal-template/universal-modal-template.types';
import { DataState } from '@shared/utils/data-state';

import { ProgramDeliveryService } from '@swagger-services/program/api/programDelivery.service';
import type { ProgramDeliveryDto } from '@swagger-services/program/model/models';

import { AddNewShippingAddressModal } from '../add-new-shipping-address/add-new-shipping-address.modal';

import type { ShippingAddressListModalContent } from './shipping-address-list-modal.types';

@Component({
  selector: 'app-shipping-address-list-modal',
  templateUrl: './shipping-address-list.modal.html',
  imports: [UniversalModalTemplate],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ContentService],
})
export class ShippingAddressListModal {
  private readonly modalService = inject(ModalService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly contentService = inject(ContentService<UniversalModalContent>);
  private readonly jexlService = inject(JexlService);
  private readonly baseActionHandler = inject(BaseActionHandlerService);
  private readonly programDeliveryService = inject(ProgramDeliveryService);

  private readonly actionHandler = this.baseActionHandler.createScope({
    selectAddress: (action) => this.handleSelectAddress(action as ProgramDeliveryDto),
    addNew: () => this.handleAddNew(),
  });

  readonly content = input<ShippingAddressListModalContent>();
  readonly addresses = input<ProgramDeliveryDto[]>([]);
  readonly programId = input.required<string>();

  protected readonly templateContent = this.contentService.rawContent;

  protected readonly isSubmitting = signal<boolean>(false);
  protected readonly isSubmitted = signal<boolean>(false);
  protected readonly error = signal<ErrorDisplayConfig | null>(null);

  protected readonly state = computed(() => {
    const template = this.templateContent();
    if (template) return DataState.success(template);
    return DataState.loading<UniversalModalContent>();
  });

  protected readonly jexlContext = computed(() => {
    const contentOverrides = this.content() ?? {};

    return {
      ...contentOverrides,
      addresses: this.addresses(),
    };
  });

  protected readonly processedGroups = computed(() => {
    const template = this.templateContent();
    const groups = template?.groups;
    if (!groups?.length) return [];
    return this.contentService.interpolateGroups(groups, this.jexlContext());
  });

  protected readonly submittingText = computed(() => {
    const template = this.templateContent();
    if (!template?.submittingText) return undefined;
    return this.jexlService.process(template.submittingText, this.jexlContext());
  });

  protected readonly submittedText = computed(() => {
    const template = this.templateContent();
    if (!template?.submittedText) return undefined;
    return this.jexlService.process(template.submittedText, this.jexlContext());
  });

  constructor() {
    afterNextRender(() => {
      this.contentService.loadModalContent('shippingAddressList').catch(console.error);
    });
  }

  protected onClose(): void {
    this.modalService.dismissLatest();
  }

  protected onRetry(): void {
    this.error.set(null);
  }

  protected onAction(action?: DynamicAction): void {
    if (action) {
      this.actionHandler.execute(action);
    }
  }

  private async handleSelectAddress(address: ProgramDeliveryDto): Promise<void> {
    this.isSubmitting.set(true);
    this.error.set(null);

    try {
      const updatedAddress = await this.programDeliveryService.programDeliveryControllerUpdate(
        '',
        this.programId(),
        address.id!,
        { ...address, isDefault: true },
      );
      this.isSubmitted.set(true);
      setTimeout(() => {
        this.modalService.dismissLatest({ selectedAddress: updatedAddress });
      }, 1500);
    } catch (error) {
      this.error.set(this.errorHandlerService.getFeedbackConfig(error));
      this.isSubmitting.set(false);
    }
  }

  private async handleAddNew(): Promise<void> {
    const programId = this.programId();
    const result = await this.modalService.openModal(AddNewShippingAddressModal, { programId }, ModalType.CARD);

    const modalResult = result as { createdAddress?: ProgramDeliveryDto } | undefined;
    if (modalResult?.createdAddress) {
      this.modalService.dismissLatest({ selectedAddress: modalResult.createdAddress });
    }
  }
}
